<script setup lang="ts">
import { useNuxtApp } from '#app'
import { NuxtLink } from '#components'
import BaseBox from '~/components/base/Box.vue'
import BaseHrTitle from '~/components/base/HrTitle.vue'
import CustomButtonMissionFavorite from '~/components/custom/ButtonMissionFavorite.vue'
import DsfrBadge from '~/components/dsfr/Badge.vue'
import DsfrHeading from '~/components/dsfr/Heading.vue'
// import DsfrLink from '~/components/dsfr/Link.vue'
import DsfrTag from '~/components/dsfr/Tag.vue'
import { useMission } from '~/composables/useMission'
import CurrentNationalOperationMissionInfos from '~/features/operations-nationales/components/CurrentNationalOperationMissionInfos.vue'
import type { Mission } from '~/types/mission'

const props = defineProps({
  mission: {
    type: Object as () => Mission,
    required: true,
  },
})

const { $filters, $stores } = useNuxtApp()

const { activities } = useMission(props.mission)
</script>

<template>
  <BaseBox class="relative z-10">
    <div class="flex justify-between mb-6 gap-2">
      <div class="flex flex-wrap gap-2 min-w-0">
        <NuxtLink
          v-if="mission.domaine"
          no-prefetch
          :to="mission.domaine.full_url"
          class="rounded-full min-w-0"
        >
          <DsfrTag size="md" context="clickable">
            {{ mission.domaine.name }}
          </DsfrTag>
        </NuxtLink>
        <NuxtLink
          v-if="mission.domaine_secondary"
          no-prefetch
          :to="mission.domaine_secondary.full_url"
          class="rounded-full min-w-0"
        >
          <DsfrTag size="md" context="clickable">
            {{ mission.domaine_secondary.name }}
          </DsfrTag>
        </NuxtLink>
      </div>

      <CustomButtonMissionFavorite v-if="$stores.auth.isLogged" :mission="mission" variant="icon" />
    </div>

    <DsfrHeading as="h2" size="xl">
      {{ mission.name ?? 'Titre à définir' }}
    </DsfrHeading>

    <p v-if="mission.responsables" class="mt-6 text-[#666666]">
      <span>Publié par </span>

      <span class="text-[#161616]">
        {{ mission.responsables.map((r) => r.secret_name).join(', ') }}
      </span>
      <span v-if="mission.structure" class="lowercase">
        de
        {{
          `${$filters.prefix(
            $filters.label(mission.structure.statut_juridique, 'structure_legal_status'),
          )} `
        }}
      </span>

      <!-- <DsfrLink
        v-if="
          mission.structure &&
          mission.structure.statut_juridique == 'Association' &&
          mission.structure.state == 'Validée'
        "
        :is-external="true"
        :to="`/organisations/${mission.structure.slug}`"
        :title="mission.structure.name"
        class="font-bold text-jva-blue-500"
      >
        {{ mission.structure.name }}
      </DsfrLink> -->

      <span class="font-bold text-[#161616]">
        {{ mission.structure?.name }}
      </span>
    </p>

    <div class="my-8 space-y-6">
      <div v-if="mission.publics_beneficiaires?.length">
        <BaseHrTitle>
          {{
            $filters.pluralize(
              mission.publics_beneficiaires.length,
              'Public aidé',
              'Publics aidés',
              false,
            )
          }}
        </BaseHrTitle>
        <div class="flex flex-wrap gap-2">
          <DsfrTag
            v-for="(publicBeneficiaire, key) in mission.publics_beneficiaires"
            :key="key"
            size="md"
          >
            {{ $filters.label(publicBeneficiaire, 'mission_publics_beneficiaires') }}
          </DsfrTag>
        </div>
      </div>

      <div v-if="activities.length > 0">
        <BaseHrTitle>
          {{ $filters.pluralize(activities.length, 'Activité', 'Activités', false) }}
        </BaseHrTitle>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="activity in activities"
            :key="activity.id"
            no-prefetch
            :to="activity.full_url"
            class="min-w-0"
          >
            <DsfrTag size="md" context="clickable">
              {{ activity.name }}
            </DsfrTag>
          </NuxtLink>
        </div>
      </div>

      <div v-if="mission.skills?.length">
        <BaseHrTitle>
          {{
            $filters.pluralize(
              mission.skills.length,
              'Compétence recherchée',
              'Compétences recherchées',
              false,
            )
          }}
        </BaseHrTitle>
        <div class="flex flex-wrap gap-2">
          <DsfrTag v-for="(skill, key) in mission.skills" :key="key" size="md">
            {{ skill.name }}
          </DsfrTag>
        </div>
      </div>
    </div>

    <CurrentNationalOperationMissionInfos :mission="mission" />

    <template v-if="mission.isFromApi">
      <hr class="border-gray-200 my-8" />

      <div>
        <h2 v-if="mission.structure" class="text-lg">
          <span>L'organisation </span>
          <b class="text-[#070191]">
            {{ mission.structure.name }}
          </b>
        </h2>
      </div>
      <div class="mt-4">
        <span>Mission proposée par </span>
        <span class="font-bold text-black">
          {{ mission.publisher_name }}
        </span>
        <div class="text-[#666666]">Vous serez redirigé vers {{ mission.publisher_url }}</div>
      </div>
    </template>
  </BaseBox>
</template>
